import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Crown, X } from "lucide-react";

/**
 * Shown when a user tries to create a new relationship but is at their
 * subscription-tier cap. FREE=3, BRONZE=12, SILVER=18, GOLD=36, PLATINUM=∞.
 * "Upgrade" navigates to /plans (Subscribe tab) with a return-to state so
 * the user lands back on /relationships after checkout.
 */
const TIER_NEXT_UP = {
  FREE: { name: "Bronze", limit: 12 },
  BRONZE: { name: "Silver", limit: 18 },
  SILVER: { name: "Gold", limit: 36 },
  GOLD: { name: "Platinum", limit: "unlimited" },
};

export default function UpgradeRequiredModal({ state, onClose }) {
  const navigate = useNavigate();
  if (!state) return null;
  const tier = (state.tier || "FREE").toUpperCase();
  const nextTier = TIER_NEXT_UP[tier] || TIER_NEXT_UP.FREE;
  const tierLabel = tier.charAt(0) + tier.slice(1).toLowerCase();

  const handleUpgrade = () => {
    onClose?.();
    navigate("/plans", { state: { from: "/relationships" } });
  };

  return (
    <Dialog open={!!state} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent
        className="bg-divine-black-light border-divine-gold/30 text-white max-w-md"
        data-testid="upgrade-required-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-divine-gold font-cinzel text-xl">
            <Crown className="w-5 h-5" />
            Relationship Limit Reached
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <p className="text-white/80 text-sm leading-relaxed">
            You've used <span className="text-divine-gold font-semibold">{state.used} of {state.limit}</span>{" "}
            relationship slots on your <span className="font-semibold">{tierLabel}</span> plan.
          </p>

          <div className="rounded-lg border border-divine-gold/20 bg-divine-gold/5 p-3">
            <p className="text-sm text-white/70">
              Upgrade to <span className="text-divine-gold font-semibold">{nextTier.name}</span> to unlock{" "}
              <span className="font-semibold">
                {nextTier.limit === "unlimited" ? "unlimited relationships" : `${nextTier.limit} relationships`}
              </span>
              .
            </p>
          </div>

          <p className="text-white/50 text-xs">
            You can also free up a slot by deleting an existing relationship.
          </p>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-white/20 text-white/70 hover:text-white hover:bg-white/5"
              data-testid="upgrade-modal-cancel"
            >
              <X className="w-4 h-4 mr-1" />
              Not now
            </Button>
            <Button
              onClick={handleUpgrade}
              className="flex-1 bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
              data-testid="upgrade-modal-upgrade"
            >
              <Crown className="w-4 h-4 mr-1" />
              Upgrade
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
